'use client';

import type { ComponentProps, ElementType, ReactNode } from 'react';

import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { menuStyles } from './menu-styles';

/**
 * Floating list of actions or options (Figma: Components ↳ Dropdown ↳ Menu),
 * on Radix DropdownMenu. Single: MenuRadioGroup - choose one, closes on
 * select. Toggles: MenuCheckboxItem. The Figma "Multi" menu - search, checkbox
 * list, Clear / Apply - is `MultiSelect`: a menu may only contain menu items,
 * so that layout is a labelled popover panel instead.
 */
export const Menu = DropdownMenu;
export const MenuTrigger = DropdownMenuTrigger;
export const MenuGroup = DropdownMenuGroup;

export type MenuContentProps = ComponentProps<typeof DropdownMenuContent> & {
  /** Match the trigger's width (Figma: attach below the trigger, same width). */
  matchTriggerWidth?: boolean;
};

export function MenuContent({
  matchTriggerWidth,
  className,
  ...props
}: MenuContentProps) {
  return (
    <DropdownMenuContent
      align="start"
      className={[
        matchTriggerWidth && 'w-(--radix-dropdown-menu-trigger-width)',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      {...props}
    />
  );
}

export type MenuItemProps = ComponentProps<typeof DropdownMenuItem> & {
  /** Figma "Type": Danger for destructive actions - place last, after a divider. */
  type?: 'default' | 'danger';
  leadingIcon?: ElementType<{ className?: string }>;
  /** Keyboard shortcut hint, e.g. "⌘K". */
  shortcut?: ReactNode;
};

export function MenuItem({
  leadingIcon: LeadingIcon,
  shortcut,
  children,
  ...props
}: MenuItemProps) {
  return (
    <DropdownMenuItem {...props}>
      {LeadingIcon && <LeadingIcon aria-hidden />}
      <span className={menuStyles().label()}>{children}</span>
      {shortcut && <DropdownMenuShortcut>{shortcut}</DropdownMenuShortcut>}
    </DropdownMenuItem>
  );
}

/** Single choice: wrap MenuRadioItems; `value` / `onValueChange` hold the choice. */
export const MenuRadioGroup = DropdownMenuRadioGroup;

export type MenuRadioItemProps = ComponentProps<typeof DropdownMenuRadioItem> & {
  leadingIcon?: ElementType<{ className?: string }>;
};

/** Figma Menu Item, Type=Default - one choice; the selected one shows a check. */
export function MenuRadioItem({
  leadingIcon: LeadingIcon,
  children,
  ...props
}: MenuRadioItemProps) {
  return (
    <DropdownMenuRadioItem {...props}>
      {LeadingIcon && <LeadingIcon aria-hidden />}
      <span className={menuStyles().label()}>{children}</span>
    </DropdownMenuRadioItem>
  );
}

/**
 * Figma Menu Item, Type=Checkbox - multi-select. Stays open on select
 * (`onSelect` is prevented) so several can be toggled.
 */
export function MenuCheckboxItem({
  onSelect,
  ...props
}: ComponentProps<typeof DropdownMenuCheckboxItem>) {
  return (
    <DropdownMenuCheckboxItem
      {...props}
      onSelect={(event) => {
        event.preventDefault();
        onSelect?.(event);
      }}
    />
  );
}

/** Figma Menu Header: an Overline title for the items below it. */
export const MenuHeader = DropdownMenuLabel;
/** Figma Menu Divider. */
export const MenuDivider = DropdownMenuSeparator;
